import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { CardTitle } from './CardComponents'
import { goalsConstants } from 'locale/goals'
import styled from 'styled-components'
import { Due, Icon, IconType } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { ProgressRing, Spinner } from 'components/generic'
import { getProgressColor } from './utils'
import { WarningFilled } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { GoalReviewStatus, Home } from '@lmc/ts-common'
import { useGoalsStats } from './hooks/useGoalsStats'
import { useNavigate } from 'react-router-dom'

const GoalsCard = () => {
  const { t } = useTranslation(NAMESPACE.GOALS)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const { data, isLoading } = useGoalsStats()
  const navigate = useNavigate()

  const navigateToGoalManagement = () => {
    navigate('reports/goals/goals-management/', {
      state: { reviewStatus: [GoalReviewStatus.overdue] },
    })
  }

  const navigateToGoalsDashboard = () => {
    navigate('reports/goals/dashboard/')
  }

  return (
    <GoalsCardContainer className="lmc-home-card">
      <CardTitle title={t(goalsConstants.GOALS)} />
      {isLoading ? (
        <Spinner style={{ paddingTop: 40 }} />
      ) : (
        <GoalsContainer>
          <StatsWrapper>
            <AlignedGroup>
              <Due />
              <Text level={1.5} weight="bold">
                {data?.residentsWithInProgressGoal || 0}
              </Text>
              <StyledText level={1.5}>
                {t(goalsConstants.SU_GOAL_IN_PROGRESS, {
                  nominative: preferredPeopleNominative,
                })}
              </StyledText>
            </AlignedGroup>
            <AlignedGroup>
              <Icon customIcon={IconType.ReportsGoals} />
              <Text level={1.5} weight="bold">
                {data?.goalsCompletedInLast30Days || 0}
              </Text>
              <StyledText level={1.5}>
                {t(goalsConstants.GOALS_COMPLETED_30_DAYS)}
              </StyledText>
            </AlignedGroup>
          </StatsWrapper>
          <Footer>
            <AlignedGroup>
              <ProgressRing
                color={getProgressColor(data?.percentageWithActiveGoal)}
                radius={45}
                progress={data?.percentageWithActiveGoal || 0}
              />
              <StyledText level={2}>
                {t(goalsConstants.SU_ACTIVE_GOAL, {
                  nominative: preferredPeopleNominative,
                })}
              </StyledText>
            </AlignedGroup>

            {data?.overdueGoals ? (
              <ClickableAlignedGroup onClick={navigateToGoalManagement}>
                <StyledWarningFilled />
                <UnderlinedText weight="medium">
                  {t(goalsConstants.NO_OVERDUE, {
                    variable: data.overdueGoals,
                  })}
                </UnderlinedText>
              </ClickableAlignedGroup>
            ) : (
              <Button variant="text" onClick={navigateToGoalsDashboard}>
                {t(goalsConstants.GOALS_DASHBOARD)}
              </Button>
            )}
          </Footer>
        </GoalsContainer>
      )}
    </GoalsCardContainer>
  )
}

const GoalsCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const GoalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const AlignedGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
const ClickableAlignedGroup = styled(AlignedGroup)`
  min-width: 120px;
  cursor: pointer;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`

const StyledWarningFilled = styled(WarningFilled)`
  color: ${({ theme }) => theme.color.status.danger};
  font-size: 24px;
`

const UnderlinedText = styled(Text)`
  text-decoration: underline;
`

export default GoalsCard
